import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  User,
} from "firebase/auth";
import { auth } from "src/lib/firebase_config";
import { initializeUserProfile } from "src/lib/userService";
import { useOperatorAuthStore } from "./operatorAuthStore";
import type { UserProfile } from "src/lib/roles";
import type { RootState } from "../store";

interface AuthState {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

const initialState: AuthState = {
  user: null,
  userProfile: null,
  loading: true,
  error: null,
  initialized: false,
};

// Async thunk for Google Sign-in
export const signInWithGoogle = createAsyncThunk(
  "auth/signInWithGoogle",
  async (_, { rejectWithValue }) => {
    try {
      const provider = new GoogleAuthProvider();
      // Force account selection every time
      provider.setCustomParameters({
        prompt: "select_account",
      });

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Initialize or get user profile from database
      const userProfile = await initializeUserProfile(
        user.uid,
        user.email!,
        user.displayName || undefined,
        user.photoURL || undefined,
      );

      return {
        user: user.toJSON() as User,
        userProfile,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to sign in");
    }
  },
);

// Async thunk for Sign out
export const signOutUser = createAsyncThunk(
  "auth/signOut",
  async (_, { rejectWithValue }) => {
    try {
      await signOut(auth);
      // Drop the iv3api operator session too so a re-login (possibly as a
      // different account) always re-verifies rather than reusing a stale token.
      useOperatorAuthStore.getState().clearToken();
      return null;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to sign out");
    }
  },
);

// Async thunk to initialize auth listener
export const initializeAuth = createAsyncThunk(
  "auth/initialize",
  async (_, { dispatch }) => {
    return new Promise<{ user: User; userProfile: UserProfile } | null>(
      async (resolve) => {
        let isFirstCall = true;

        // Set up the auth state listener - this remains active throughout the app lifecycle
        onAuthStateChanged(auth, async (user) => {
          // Presence signs in anonymously for its RTDB connection
          // (`ensureAnonymousAuth`). That must never count as an app session —
          // otherwise it resurrects a "logged in" user right after logout and
          // bounces the login page back into the auth-pending overlay.
          const appUser = user && !user.isAnonymous ? user : null;
          if (isFirstCall) {
            isFirstCall = false;
            if (appUser) {
              try {
                // Load user profile from database
                const userProfile = await initializeUserProfile(
                  appUser.uid,
                  appUser.email!,
                  appUser.displayName || undefined,
                  appUser.photoURL || undefined,
                );
                resolve({
                  user: appUser.toJSON() as User,
                  userProfile,
                });
              } catch (error) {
                console.error("Error loading user profile:", error);
                resolve(null);
              }
            } else {
              resolve(null);
            }
          } else {
            // Subsequent calls - update the Redux state directly
            if (appUser) {
              try {
                const userProfile = await initializeUserProfile(
                  appUser.uid,
                  appUser.email!,
                  appUser.displayName || undefined,
                  appUser.photoURL || undefined,
                );
                dispatch(
                  authStateChanged({
                    user: appUser.toJSON() as User,
                    userProfile,
                  }),
                );
              } catch (error) {
                console.error("Error loading user profile:", error);
                dispatch(authStateChanged(null));
              }
            } else {
              dispatch(authStateChanged(null));
            }
          }
        });
      },
    );
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    // Action to handle auth state changes from the listener
    authStateChanged: (state, action) => {
      if (action.payload) {
        state.user = action.payload.user;
        state.userProfile = action.payload.userProfile;
      } else {
        state.user = null;
        state.userProfile = null;
      }
      state.loading = false;
    },
    // Action to update user profile without re-authentication
    updateUserProfile: (state, action) => {
      state.userProfile = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Sign in with Google
      .addCase(signInWithGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signInWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.userProfile = action.payload.userProfile;
        state.error = null;
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Sign out
      .addCase(signOutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(signOutUser.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.userProfile = null;
        state.error = null;
      })
      .addCase(signOutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Initialize auth
      .addCase(initializeAuth.pending, (state) => {
        state.loading = true;
        state.initialized = false;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.user = action.payload.user;
          state.userProfile = action.payload.userProfile;
        }
        state.initialized = true;
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.loading = false;
        state.initialized = true;
      });
  },
});

export const { clearError, authStateChanged, updateUserProfile } =
  authSlice.actions;

// Selectors
export const selectUser = (state: RootState) => state.auth.user;
export const selectUserProfile = (state: RootState) => state.auth.userProfile;
export const selectAuthLoading = (state: RootState) => state.auth.loading;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectAuthInitialized = (state: RootState) =>
  state.auth.initialized;

export default authSlice.reducer;
